import Vue from 'vue'
import Vuex, {Store} from 'vuex'
import router from '../router'
import auth, {State as AuthState} from './modules/auth'
import pref, {State as PrefState} from './modules/pref'
import sys, {State as SysState} from './modules/sys'
import device, {State as DeviceState, MatrixMap} from './modules/device'
import common, {State as CommonState} from './modules/common'
import { IVW } from '@/types/model'
import { Matrix } from '@/types/api'

// ====================================================
// Types
// ====================================================
interface Dictionary<T> { [key: string]: T }

interface RouterState { // Define this manually for vuex-router-sync
  name: string
  path: string
  fullPath: string
  hash: string
  params: Dictionary<string>
  query: Dictionary<string>
  meta: any
  from?: any // I'm lazy...
}

export interface State {
  auth: AuthState
  pref: PrefState
  common: CommonState
  device: DeviceState
  sys: SysState
}

// ====================================================
// Main
// ====================================================

Vue.use(Vuex)

const store = new Vuex.Store({
  modules: {
    device,
    auth,
    common,
    pref,
    sys
  }
}) as Store<State>

export default store

// ====================================================
// Type-awared Mutations / Actions via a proxy function
// ====================================================

// TypeOfArg1<typeof FUNCTION>
type TypeOfArg1<T> = T extends (arg1: infer U, ...args: any[]) => any ? U : any
type TypeOfArg2<T> = T extends (arg1: any, arg2: infer U, ...args: any[]) => any ? U : any
type TypeOfReturnValue<T> = T extends (...args: any[]) => infer R ? R : any
/**
 *  Concept: This will actually extract the type of parameters of function, the function itself is not used.
 */
// tslint:disable-next-line:ban-types
function genMutFn<F extends Function> (moduleName: string, _rawFn: F): (payload: TypeOfArg2<F>) => any {
  return (payload) => store.commit(`${moduleName}/${_rawFn.name}`, payload)
}
// tslint:disable-next-line:ban-types
function genActFn0<F extends Function> (moduleName: string,  _rawFn: F): () => any {
  return () => store.dispatch(`${moduleName}/${_rawFn.name}`)
}
// tslint:disable-next-line:ban-types
function genActFn1<F extends Function> (moduleName: string, _rawFn: F): (payload: TypeOfArg2<F>) => any {
  return (payload) => store.dispatch(`${moduleName}/${_rawFn.name}`, payload)
}
export const $s = {
  $state: store.state as State,
  common: {
    mutations: {
      assignNetworkError: genMutFn('common', common.mutations.assignNetworkError),
      assignWindowSize: genMutFn('common', common.mutations.assignWindowSize),
    }
  },
  device: {
    getters: {
      get matrixMap (): MatrixMap { return store.getters['device/matrixMap'] },
      get matrixList (): Matrix[] { return store.getters['device/matrixList'] },
      get vwList (): IVW[] { return store.getters['device/vwList'] }
    },
    mutations: {
      assignDev: genMutFn('device', device.mutations.assignDev),
      assignDevFromWs: genMutFn('device', device.mutations.assignDevFromWs),
      assignList: genMutFn('device', device.mutations.assignList),
      assignListWithoutBreakReferences: genMutFn('device', device.mutations.assignListWithoutBreakReferences),
      assignVw: genMutFn('device', device.mutations.assignVw),
      assignVwList: genMutFn('device', device.mutations.assignVwList),
      addVw: genMutFn('device', device.mutations.addVw),
      deleteVw: genMutFn('device', device.mutations.deleteVw),
      assignOsdList: genMutFn('device', device.mutations.assignOsdList),
      assignOrCreateOsd: genMutFn('device', device.mutations.assignOrCreateOsd),
      assignOsdImgList: genMutFn('device', device.mutations.assignOsdImgList),
      assignOrCreateOsdImg: genMutFn('device', device.mutations.assignOrCreateOsdImg),
      deleteOsd: genMutFn('device', device.mutations.deleteOsd),
      deleteOsdImg: genMutFn('device', device.mutations.deleteOsdImg),
      assignPresetList: genMutFn('device', device.mutations.assignPresetList),
      assignOrCreatePreset: genMutFn('device', device.mutations.assignOrCreatePreset),
      deletePreset: genMutFn('device', device.mutations.deletePreset),
      _firstTimeFetched: genMutFn('device', device.mutations._firstTimeFetched),
    }
  },
  pref: {
    mutations: {
      assignStateSafely: genMutFn('pref', pref.mutations.assignStateSafely),
      assignLang: genMutFn('pref', pref.mutations.assignLang),
      assignDeviceList: genMutFn('pref', pref.mutations.assignDeviceList),
      assignMatrix: genMutFn('pref', pref.mutations.assignMatrix),
      assignVW: genMutFn('pref', pref.mutations.assignVW),
    }
  },
  auth: {
    mutations: {
      assignLoginState: genMutFn('auth', auth.mutations.assignLoginState),
    }
  },
  sys: {
    mutations: {
      assignBasicInfo: genMutFn('sys', sys.mutations.assignBasicInfo),
      assignNetworkSetting: genMutFn('sys', sys.mutations.assignNetworkSetting),
      assignNetworkStatus: genMutFn('sys', sys.mutations.assignNetworkStatus),
    }
  }
}
